import { useMemo } from "react";
import { __ } from "services/Translator";
import { useBonusData, buildBonusOptions } from "./useCashierData";
import InfoCard from "./InfoCard";
import { BonusIcoIcon, BoltIcon } from "./icons";

export default function BonusesView({ cashier = {}, onClaim }) {
  const { active, campaigns, loading } = useBonusData();
  const formatMoney = useMemo(() => cashier.formatMoney || ((v) => String(v)), [cashier.formatMoney]);

  const hasActive = !!(active && active.has_active_bonus);
  const bonusBalance = active ? Number(active.bonus_balance) || 0 : 0;
  const requirement = active ? Number(active.wagering_requirement) || 0 : 0;
  const achieved = active ? Number(active.wagering_achieved) || 0 : 0;
  const pct = requirement > 0 ? Math.min(100, Math.round((achieved / requirement) * 100)) : 0;
  const remaining = Math.max(0, requirement - achieved);

  // Eligible bonuses only (drop the synthetic "No bonus" option from the list).
  // fmt is threaded so the bonus minimum deposit carries the player currency
  // symbol instead of a bare integer.
  const available = useMemo(
    () => buildBonusOptions(campaigns, formatMoney).filter((o) => o.id !== "no_bonus"),
    [campaigns, formatMoney]
  );

  // Client-side handoff ONLY: the deposit submit already sends the campaign id
  // explicitly, so persisting next_deposit_bonus server-side here would leave a
  // stale row that a later, unrelated deposit path could silently consume.
  const claim = (id) => {
    if (typeof onClaim === "function") onClaim(id);
  };

  return (
    <>
      <InfoCard gold style={{ marginBottom: "18px" }}>
        <div className="ic-top">
          <span className="ic-ico">
            <BonusIcoIcon />
          </span>
          <div className="tt">
            <b>{__("Bonus balance")}</b>
            <small>
              {loading && !active ? __("Loading...") : hasActive ? __("Active bonus") : __("No active bonus")}
            </small>
          </div>
          <span className="amt">
            {loading && !active ? <b className="skel" aria-hidden="true" /> : formatMoney(bonusBalance)}
          </span>
        </div>
        {hasActive && requirement > 0 ? (
          <>
            <div className="progress">
              <i style={{ width: pct + "%" }} />
            </div>
            <div className="prog-lbl">
              <span>
                {__("Wagering")} <b>{formatMoney(achieved)}</b> / {formatMoney(requirement)}
              </span>
              <span>{pct}%</span>
            </div>
            <div className="note-line" style={{ marginTop: "12px", fontSize: "11.5px" }}>
              {__("Wager")} <b>{formatMoney(remaining)}</b>{" "}
              {__("more to unlock this bonus for withdrawal.")}
            </div>
          </>
        ) : null}
      </InfoCard>

      <div className="field-lbl">{__("Available bonuses")}</div>
      <div className="stack">
        {loading ? (
          [0, 1].map((i) => (
            <InfoCard key={`bonus-skel-${i}`}>
              <div className="ic-top">
                <span className="ic-ico em">
                  <BoltIcon />
                </span>
                <div className="tt">
                  <b className="skel" aria-hidden="true" />
                  <small className="skel" aria-hidden="true" />
                </div>
              </div>
            </InfoCard>
          ))
        ) : available.length ? (
          available.map((opt, i) => (
            <InfoCard key={`${opt.id != null ? opt.id : "bonus"}-${i}`}>
              <div className="ic-top">
                <span className="ic-ico em">
                  <BoltIcon />
                </span>
                <div className="tt">
                  <b>{opt.title}</b>
                  <small>{opt.sub}</small>
                </div>
                <button className="miniclaim" onClick={() => claim(opt.id)}>
                  {__("Claim")}
                </button>
              </div>
            </InfoCard>
          ))
        ) : (
          <InfoCard>
            <div className="note-line">
              {__("There are currently no active deposit bonus campaigns")}
            </div>
          </InfoCard>
        )}
      </div>
    </>
  );
}
